import { buildApiRequestHeaders, forwardCookiesToClient, isAbortError, isInternalUrl, OpenapiError, useApm, useCoreLogger, useNuxtApp, useRequestEvent, useRequestHeaders, useRequestURL, useRuntimeConfig } from '#imports'
import type { ClientOptions as OpenapiClientOptions, Client as OpenapiClient, Middleware as OpenapiMiddleware } from 'openapi-fetch'
import createClient from 'openapi-fetch'
import { joinURL } from 'ufo'
import { defu } from 'defu'
import { once } from 'es-toolkit'
import type { NitroFetchOptions } from 'nitropack'
import type { UseOpenapiOptions } from '../../types'
import { captureHttpError } from '../../shared/api/mutationErrorCapture'
import { redirectUnauthorized } from '../../shared/api/unauthorizedRedirect'
import { coreServerConfig } from '../../utils/coreServerConfig'

export const useOpenapi = <Paths extends object>(options: UseOpenapiOptions): OpenapiClient<Paths> => {
    const { enableCookieForwarding = true, ignoreNotFound = false, ignoreUnauthorized = false, middleware: userMiddleware, reportErrors = true, service, ...fetchOptions } = options

    // Service config is private, so only the server can read it.
    const serviceConfig = import.meta.server && service ? coreServerConfig(useRuntimeConfig()).services?.[service] : undefined
    const event = useRequestEvent()
    const url = useRequestURL()
    const requestHeaders = useRequestHeaders()
    const apm = useApm()
    const { $i18n } = useNuxtApp()
    const getLogger = once(() => useCoreLogger().withTag('useOpenapi'))

    let baseUrl = fetchOptions.baseUrl

    if (serviceConfig?.server?.baseUrl) {
        baseUrl = joinURL(serviceConfig.server.baseUrl, baseUrl || '')
    }

    if (!baseUrl) {
        baseUrl = `${useRequestURL().origin}`
    }

    const preparedOptions = defu<OpenapiClientOptions, OpenapiClientOptions[]>({
        baseUrl,
        credentials: 'include',
    },
    fetchOptions,
    {
        fetch: async (input) => {
            const response = await $fetch.raw(input, {
                ...input,
                method: input.method as NitroFetchOptions<any>['method'] ?? 'GET',
                ignoreResponseError: true,
                responseType: 'stream',
            })

            return response
        }
    })

    const client = createClient<Paths>(preparedOptions)

    // Add middleware
    const middleware: OpenapiMiddleware = {
        onRequest(context) {
            if (import.meta.server) {
                const url: string = context.request.url

                let isOutsideInternalNetwork = false

                if (url.startsWith('/')) {
                    if (!isInternalUrl(baseUrl)) {
                        isOutsideInternalNetwork = true
                    }
                }
                else if (!isInternalUrl(url)) {
                    isOutsideInternalNetwork = true
                }

                if (isOutsideInternalNetwork) {
                    getLogger().warn({
                        message: `Request made outside of the internal network. Url is: ${url}. baseURL is: ${baseUrl}`,
                        isOutsideInternalNetwork
                    })
                }
            }

            const preparedHeaders = buildApiRequestHeaders({
                event: event!,
                requestHeaders,
                extraHeaders: {
                    ...fetchOptions.headers,
                    ...Object.fromEntries(context.request.headers),
                },
            })

            preparedHeaders.forEach((value, key) => {
                context.request.headers.set(key, value)
            })

            if (userMiddleware?.onRequest) {
                return userMiddleware.onRequest(context)
            }

            return context.request
        },
        async onResponse(context) {
            if (import.meta.server) {
                if (enableCookieForwarding) {
                    forwardCookiesToClient(event!, context.response.headers)
                }
            }

            const isIgnoredUnauthorized = ignoreUnauthorized && context.response?.status === 401

            if (import.meta.client && context.response?.status === 401 && !isIgnoredUnauthorized) {
                await redirectUnauthorized($i18n.t('pages.lobby'), context.request.url)
            }

            if (!context.response.ok) {
                let _data: unknown

                try {
                    _data = await context.response.clone().json()
                }
                catch {
                    _data = await context.response.clone().text()
                }

                const error = new OpenapiError({
                    ...context,
                    response: Object.assign(context.response, { _data })
                })

                const isIgnoredNotFound = ignoreNotFound && context.response.status === 404

                if (reportErrors && !isIgnoredUnauthorized && !isIgnoredNotFound) {
                    if (import.meta.server) {
                        getLogger().error(error)
                    }
                    else {
                        captureHttpError({ error, response: context.response }, () => apm.captureError(error))
                    }
                }

                throw error
            }

            if (userMiddleware?.onResponse) {
                return userMiddleware.onResponse(context)
            }

            return context.response
        },
        onError(context) {
            if (OpenapiError.isError(context?.error)) {
                return
            }

            if (isAbortError(context?.error)) return

            const error = new OpenapiError(context)

            if (reportErrors) {
                if (import.meta.server) {
                    getLogger().error(error)
                }
                else {
                    captureHttpError({ error }, () => apm.captureError(error))
                }
            }

            throw error
        }
    }

    client.use(middleware)

    return client
}
